"use strict";
/*
 * Copyright (c) "Neo4j"
 * Neo4j Sweden AB [http://neo4j.com]
 *
 * This file is part of Neo4j.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AggregationFilter = void 0;
const cypher_builder_1 = __importDefault(require("@neo4j/cypher-builder"));
const InterfaceEntityAdapter_1 = require("../../../../../schema-model/entity/model-adapters/InterfaceEntityAdapter");
const create_node_from_entity_1 = require("../../../utils/create-node-from-entity");
const Filter_1 = require("../Filter");
class AggregationFilter extends Filter_1.Filter {
    constructor(relationship, isDeprecated = false, attachedTo) {
        super();
        this.relationship = relationship;
        this.isDeprecated = isDeprecated;
        this.attachedTo = attachedTo;
    }
    getSubqueries(context) {
        if (!context.hasTarget()) {
            throw new Error("No parent node found!");
        }
        this.subqueryReturnVariable = new cypher_builder_1.default.Variable();
        const relatedEntity = this.relationship.target;
        const relatedNode = new cypher_builder_1.default.Node();
        let relatedNodeLabels = [];
        let labelsFilter;
        if (relatedEntity instanceof InterfaceEntityAdapter_1.InterfaceEntityAdapter) {
            const labelsForImplementations = relatedEntity.concreteEntities.map((e) => relatedNode.hasLabels(...e.getLabels()));
            labelsFilter = cypher_builder_1.default.or(...labelsForImplementations);
        }
        else {
            relatedNodeLabels = (0, create_node_from_entity_1.getEntityLabels)(relatedEntity, context.neo4jGraphQLContext);
        }
        const relationshipTarget = new cypher_builder_1.default.Relationship();
        const pattern = new cypher_builder_1.default.Pattern(context.target)
            .related(relationshipTarget, {
            direction: this.relationship.getCypherDirection(),
            type: this.relationship.type,
        })
            .to(relatedNode, { labels: relatedNodeLabels });
        const nestedContext = context.push({
            target: relatedNode,
            relationship: relationshipTarget,
        });
        const returnColumns = [];
        const predicate = this.getSubqueryReturnVariable(nestedContext);
        if (predicate) {
            returnColumns.push([predicate, this.subqueryReturnVariable]);
        }
        if (returnColumns.length === 0) {
            throw new Error(`Transpile error: An error occurred when trying to get the predicate for the ${this.constructor.name}`);
        }
        const match = this.shouldApplyDistinct()
            ? new cypher_builder_1.default.Match(pattern).with(nestedContext.target).distinct()
            : new cypher_builder_1.default.Match(pattern);
        const subquery = labelsFilter
            ? match.where(labelsFilter).return(...returnColumns)
            : match.return(...returnColumns);
        return [subquery];
    }
    getPredicate() {
        if (this.subqueryReturnVariable) {
            return cypher_builder_1.default.eq(this.subqueryReturnVariable, cypher_builder_1.default.true);
        }
        throw new Error(`Transpile error: An error occurred when trying to get the predicate for the ${this.constructor.name}`);
    }
    createBaseOperation({ operator, expr, param, }) {
        switch (operator) {
            case "LT":
                return cypher_builder_1.default.lt(expr, param);
            case "LTE":
                return cypher_builder_1.default.lte(expr, param);
            case "GT":
                return cypher_builder_1.default.gt(expr, param);
            case "GTE":
                return cypher_builder_1.default.gte(expr, param);
            case "IN":
                return cypher_builder_1.default.in(expr, param);
            case "EQUAL":
            case "EQ":
                return cypher_builder_1.default.eq(expr, param);
            default:
                throw new Error(`Invalid operator ${operator}`);
        }
    }
    shouldApplyDistinct() {
        // DISTINCT is only applied on aggregations inside Connections input
        return !this.isDeprecated && this.attachedTo === "node";
    }
}
exports.AggregationFilter = AggregationFilter;
//# sourceMappingURL=AggregationFilter.js.map